import { GatedQueryError } from "../components/GatedQueryError";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Card,
  Layout,
  Page,
  Select,
  SkeletonBodyText,
  Text,
} from "@shopify/polaris";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getDeadStock,
  getInventoryAnalytics,
  type AbcAnalysis,
  type AbcLine,
  type DeadStockReport,
  type InventoryValuation,
} from "../api/inventory_analytics";
import { listLocations } from "../api/locations";
import { getPlanPreview } from "../api/plan_previews";
import { toLocationSelectOptions } from "../utils/locationOptions";
import { getMe } from "../api/me";
import { CappedDataTable } from "../components/CappedDataTable";
import { PlanDataPreviewGate } from "../components/PlanDataPreviewGate";
import { UpgradeNote } from "../components/PlanGate";
import { formatNumber, money } from "../utils/money";
import { useCurrency } from "../lib/useCurrency";

import { t } from "../lib/i18n";
import { useLocationLimit } from "../lib/useLocationLimit";

/**
 * 拠点セレクタは在庫評価額にしか効かない（ABC分析の売上は拠点を持たないため
 * バックエンドが常に全店合算で返す）。黙って効かないと「拠点を絞ったのにABCが
 * 変わらない＝バグ」と誤解されるので明示する。
 */
// 🔴 モジュール直下で t() を呼ぶと initLocale() より前に評価され locale が効かない
// （SERVICE_LEVEL_OPTIONS で踏んだ罠と同型）＝JA 原文だけ持ち、使う側で t() に包む。
const ABC_ALL_LOCATIONS_NOTE =
  "ABC分析だけは常に全拠点合算です（売上データが拠点を持たないため）。在庫評価額と不動在庫は、選んだ拠点の在庫で計算します。";

/** 複数ロケ店とみなす拠点数。1拠点なら絞る余地が無いのでセレクタを出さない。 */
const MULTI_LOCATION_MIN_COUNT = 2;

/**
 * 少数SKU店でABC表を畳むときの注記。取扱商品が少ないとパレート分析が偏る旨を
 * マーチャント向けに説明する（この文言はプロダクト仕様として固定）。
 */
const ABC_FOLD_ANNOTATION =
  "取扱商品が少ないため、ABCランクは参考値です。ABC分析は売上の偏り（パレートの法則）を見る手法で、商品数が少ないと『一番の売れ筋でもCランク』のように偏った結果になります。商品が増えるほど精度が上がります。";

/** ランク別のバッジ配色。A=好調(success)/B=info/C=中立(既定tone)。 */
const RANK_TONE: Record<AbcLine["rank"], "success" | "info" | undefined> = {
  A: "success",
  B: "info",
  C: undefined,
};

/** 累積構成比（0〜1の文字列）を「73%」形式へ。 */
/** 店の通貨で金額を整形するフック（`["me"]` を共有＝追加フェッチなし）。 */
function useMoney() {
  const currency = useCurrency();
  return (amount: string | number) => money(amount, currency);
}

function formatShare(share: string): string {
  return `${Math.round(Number(share) * 100)}%`;
}

type LocationFilterProps = {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
  /** 複数ロケ店だが Business 未満なら true（無効化＝実質、主拠点固定）。 */
  disabled: boolean;
  /** disabled のときだけ表示する主拠点名（UpgradeNote の補足に使う）。 */
  primaryLocationName: string | null;
};

/**
 * 拠点フィルタ。複数ロケ店にだけ出す（Business 機能）。Business 未満でも隠さず
 * 無効化して出す — 「なぜ拠点別に見られないのか」を UpgradeNote と共に示すため。
 */
function LocationFilter({
  options,
  value,
  onChange,
  disabled,
  primaryLocationName,
}: LocationFilterProps) {
  return (
    <Card>
      <BlockStack gap="300">
        <Box maxWidth="280px">
          <Select
            label={t("拠点")}
            options={options}
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
        </Box>
        <Text as="span" variant="bodySm" tone="subdued">
          {t(ABC_ALL_LOCATIONS_NOTE)}
        </Text>
        {disabled && (
          <UpgradeNote feature="inventory_transfer">
            <p>
              {t("現在は主拠点（{name}）のみの在庫評価額です。", {
                name: primaryLocationName ?? t("既定の配送先"),
              })}
              {t("他の拠点も含めるには、拠点数の多いプランへの変更が必要です。")}
            </p>
          </UpgradeNote>
        )}
      </BlockStack>
    </Card>
  );
}

/**
 * 初回同期がまだの店に出す1枚バナー。「同期前」と「同期済みだが本当に0」を
 * 区別できないのが従来の空バナーの穴だった（bug-hunt H群）＝空状態には必ず
 * 行き先を添える。3セクションぶんの空バナーを並べず、行き先つきの1枚に畳む。
 */
function NotSyncedBanner({ onGoToDashboard }: { onGoToDashboard: () => void }) {
  return (
    <Banner
      tone="info"
      title={t("商品がまだ同期されていません")}
      action={{ content: t("同期する"), onAction: onGoToDashboard }}
    >
      <p>{t("最初の同期が完了すると、在庫評価額・ABC分析・滞留在庫がここに表示されます。")}</p>
    </Banner>
  );
}

function ValuationSection({ valuation }: { valuation: InventoryValuation }) {
  const yen = useMoney();
  const rows = valuation.lines.map((line) => [
    line.title ?? "—",
    line.sku ?? "—",
    formatNumber(line.available),
    line.is_estimated
      ? t("{amount}（目安）", { amount: yen(line.unit_cost) })
      : yen(line.unit_cost),
    yen(line.line_value),
  ]);

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">
            {t("在庫評価額")}
          </Text>
          <Text as="p" variant="heading2xl">
            {yen(valuation.total_value)}
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            {t("原価（手入力 > Shopify原価 > 販売価格の順）× 在庫数の合計です。")}
          </Text>
        </BlockStack>
      </Card>

      {valuation.has_estimated_lines && (
        <Banner tone="warning" title={t("仕入単価は目安です")}>
          <p>
            {t("一部の商品はShopifyに仕入原価が未設定のため、販売価格を目安に評価額を計算しています（「目安」表示）。 Shopify商品の原価（原価管理）を設定すると正確な評価額になります。")}
          </p>
        </Banner>
      )}

      {valuation.lines.length === 0 ? (
        <Banner tone="info" title={t("在庫のある商品がありません")}>
          <p>{t("在庫数が1以上の商品が同期されると、ここに評価額の内訳が表示されます。")}</p>
        </Banner>
      ) : (
        <CappedDataTable
          columnContentTypes={["text", "text", "numeric", "numeric", "numeric"]}
          headings={[t("商品名"), "SKU", t("在庫"), t("単価"), t("評価額")]}
          rows={rows}
        />
      )}
    </BlockStack>
  );
}

function AbcTable({ lines }: { lines: AbcLine[] }) {
  const yen = useMoney();
  const rows = lines.map((line) => [
    line.title ?? "—",
    line.sku ?? "—",
    formatNumber(line.quantity_sold),
    yen(line.sales_value),
    formatShare(line.cumulative_share),
    <Badge key={`rank-${line.variant_id}`} tone={RANK_TONE[line.rank]}>
      {line.rank}
    </Badge>,
  ]);

  return (
    <CappedDataTable
      columnContentTypes={["text", "text", "numeric", "numeric", "numeric", "text"]}
      headings={[t("商品名"), "SKU", t("販売数"), t("売上"), t("累積構成比"), t("ランク")]}
      rows={rows}
    />
  );
}

function AbcSection({ abc }: { abc: AbcAnalysis }) {
  const header = (
    <Text as="h2" variant="headingMd">
      {t("ABC分析（売れ筋ランク）")}
    </Text>
  );

  if (abc.lines.length === 0) {
    return (
      <BlockStack gap="400">
        {header}
        <Banner tone="info" title={t("対象期間の売上がありません")}>
          <p>{t("直近の販売実績がある商品が同期されると、ここにABCランクが表示されます。")}</p>
        </Banner>
      </BlockStack>
    );
  }

  return (
    <BlockStack gap="400">
      {header}
      {!abc.is_meaningful && (
        <Banner tone="info" title={t("ABCランクは参考値です")}>
          <p>{t(ABC_FOLD_ANNOTATION)}</p>
        </Banner>
      )}
      <AbcTable lines={abc.lines} />
    </BlockStack>
  );
}

/** 最終販売からの経過日数を「45日前」/「販売実績なし」へ整形する。 */
function formatDaysSince(days: number | null): string {
  return days === null
    ? t("販売実績なし")
    : t("{n}日前", { n: formatNumber(days) });
}

function DeadStockSection({ report }: { report: DeadStockReport }) {
  const yen = useMoney();
  const header = (
    <BlockStack gap="100">
      <Text as="h2" variant="headingMd">
        {t("デッドストック（滞留在庫）")}
      </Text>
      <Text as="span" variant="bodySm" tone="subdued">
        {t("直近{days}日まったく売れていないのに在庫が残っている商品です（寝ている仕入資金の大きい順）。", {
          days: report.window_days,
        })}
      </Text>
    </BlockStack>
  );

  if (report.lines.length === 0) {
    return (
      <BlockStack gap="400">
        {header}
        <Banner tone="success" title={t("滞留在庫はありません")}>
          <p>
            {t("直近{days}日で、在庫が残ったまま一度も売れていない商品は見つかりませんでした。", {
              days: report.window_days,
            })}
          </p>
        </Banner>
      </BlockStack>
    );
  }

  const rows = report.lines.map((line) => [
    line.title ?? "—",
    line.sku ?? "—",
    formatNumber(line.available),
    line.is_estimated
      ? t("{amount}（目安）", { amount: yen(line.unit_cost) })
      : yen(line.unit_cost),
    yen(line.tied_value),
    formatDaysSince(line.days_since_last_sale),
  ]);

  return (
    <BlockStack gap="400">
      {header}
      <Card>
        <BlockStack gap="100">
          <Text as="span" variant="bodySm" tone="subdued">
            {t("寝ている仕入資金の合計")}
          </Text>
          <Text as="p" variant="headingXl">
            {yen(report.total_tied_value)}
          </Text>
        </BlockStack>
      </Card>
      {report.has_estimated_lines && (
        <Banner tone="warning" title={t("仕入単価は目安です")}>
          <p>{t("一部の商品は原価が未設定のため、販売価格を目安に滞留資金を計算しています（「目安」表示）。")}</p>
        </Banner>
      )}
      <CappedDataTable
        columnContentTypes={["text", "text", "numeric", "numeric", "numeric", "text"]}
        headings={[t("商品名"), "SKU", t("在庫"), t("単価"), t("滞留資金"), t("最終販売")]}
        rows={rows}
      />
    </BlockStack>
  );
}

const FEATURE_NAME = "在庫分析";

export function InventoryAnalytics() {
  return (
    <PlanDataPreviewGate
      feature="inventory_analytics"
      featureName={t(FEATURE_NAME)}
      previewQueryKey={["plan-preview", "inventory-analytics"]}
      previewQueryFn={() =>
        Promise.all([
          getPlanPreview("inventory_valuation"),
          getPlanPreview("inventory_abc_30d"),
          getPlanPreview("inventory_dead_stock_90d"),
        ])
      }
      sections={[
        {
          dataset: "inventory_valuation",
          title: t("在庫評価額の商品"),
          columns: [
            { key: "product", label: t("商品") },
            { key: "sku", label: "SKU" },
            { key: "available", label: t("在庫") },
            { key: "unit_cost", label: t("単価") },
            { key: "line_value", label: t("評価額") },
          ],
        },
        {
          dataset: "inventory_abc_30d",
          title: t("直近30日のABC分析商品"),
          columns: [
            { key: "product", label: t("商品") },
            { key: "sku", label: "SKU" },
            { key: "quantity_sold", label: t("販売数") },
            { key: "sales_value", label: t("売上") },
            { key: "cumulative_share", label: t("累積構成比") },
            { key: "rank", label: t("ランク") },
          ],
        },
        {
          dataset: "inventory_dead_stock_90d",
          title: t("90日間の滞留在庫商品"),
          columns: [
            { key: "product", label: t("商品") },
            { key: "sku", label: "SKU" },
            { key: "available", label: t("在庫") },
            { key: "unit_cost", label: t("単価") },
            { key: "tied_value", label: t("滞留資金") },
            { key: "last_sale", label: t("最終販売") },
          ],
        },
      ]}
    >
      <InventoryAnalyticsContent />
    </PlanDataPreviewGate>
  );
}

/**
 * ゲート通過後の本体。プラン判定（loading/error/未許可）は PlanDataPreviewGate が担うので、
 * ここへ来た時点で inventory_analytics は許可済み。データ取得用の ["me"] 観測は
 * この本体がマウントされて初めて張られる（プラン取得中/失敗時には張られない）。
 * これでプラン取得側の ["me"] クエリと enabled が食い違い、全API障害時に isLoading が
 * error へ落ちず固着する事故（在庫分析だけスケルトンのまま行き止まり）を防ぐ。
 */
function InventoryAnalyticsContent() {
  const navigate = useNavigate();
  // 🔴 拠点は「機能」ではなく「数の階段」＝上限が2以上なら他拠点を見せる
  //    （評価額そのものは Pro の権利で、こことは別ゲート）。
  const locationLimit = useLocationLimit();
  const canMultiLocation =
    locationLimit === undefined || locationLimit.limit === null || locationLimit.limit > 1;

  // 絞り込み中の拠点。null = 未選択（バックエンドが主拠点へ解決する。全拠点
  // 合算はもう既定ではない＝配送先 Phase2 ⑤）。
  const [locationId, setLocationId] = useState<string | null>(null);

  // 拠点セレクタの出し分け（単一ロケ店には出さない）。/api/me の要約から判定する。
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const locationCount = meQuery.data?.locations?.count ?? 0;
  const primaryLocationId = meQuery.data?.locations?.primary_location_id ?? null;
  const primaryLocationName = meQuery.data?.locations?.primary_name ?? null;
  const isMultiLocation = locationCount >= MULTI_LOCATION_MIN_COUNT;

  const locationsQuery = useQuery({
    queryKey: ["analytics-locations"],
    queryFn: listLocations,
    // /api/locations は Business ゲート（403）。未満では取得しない。
    enabled: canMultiLocation && isMultiLocation,
  });

  // 拠点指定はBusinessでしか通らない（未満は 403）。セッション中にプランが落ちた
  // ときに選択が残っていても null（＝主拠点へバックエンドが解決）へ倒し、
  // 生の403ではなくアップセルを見せる。
  const effectiveLocationId = canMultiLocation ? locationId : null;

  const analyticsQuery = useQuery({
    queryKey: ["inventory-analytics", effectiveLocationId],
    queryFn: () => getInventoryAnalytics(effectiveLocationId),
    // 拠点を切り替えても前回値を残す（スケルトンへ戻さずセレクタを操作し続けられる）。
    placeholderData: keepPreviousData,
  });

  const deadStockQuery = useQuery({
    queryKey: ["dead-stock", effectiveLocationId],
    queryFn: () => getDeadStock(effectiveLocationId),
    placeholderData: keepPreviousData,
  });

  // 拠点の選択肢。全拠点合算はもう選択肢に無い（配送先 Phase2 ⑤）。
  // Business は /api/locations の全件（主拠点含む）、未満は主拠点1件だけを
  // 合成して出す（/api/locations 自体が 403 で引けないため）。
  const locationOptions = canMultiLocation
    ? toLocationSelectOptions(locationsQuery.data ?? [])
    : primaryLocationId !== null && primaryLocationName !== null
      ? [{ label: primaryLocationName, value: String(primaryLocationId) }]
      : [];

  // 未選択(null)なら主拠点を選択中として表示する（バックエンドが実際に解決する拠点と一致）。
  const selectedLocationValue =
    locationId !== null
      ? String(locationId)
      : primaryLocationId !== null
        ? String(primaryLocationId)
        : "";

  const handleLocationChange = (value: string) => setLocationId(value);

  // 「同期前」と「同期済みだが本当に0」を区別する（従来は同じ空バナーで、
  // 次の一手＝同期がマーチャントに伝わらなかった）。/api/me が未着のあいだは
  // true 側（穏当な従来表示）へ倒し、未同期バナーを一瞬チラつかせない。
  const hasSynced = meQuery.data ? meQuery.data.last_sync_at !== null : true;
  const showNotSynced =
    !hasSynced &&
    analyticsQuery.data !== undefined &&
    analyticsQuery.data.valuation.lines.length === 0 &&
    analyticsQuery.data.abc_analysis.lines.length === 0;

  return (
    <Page
      title={t("在庫分析")}
      subtitle={t("在庫評価額と売れ筋（ABC分析）で在庫を金額と貢献度の2軸で見える化します")}
    >
      <Layout>
        {isMultiLocation && (
          <Layout.Section>
            <LocationFilter
              options={locationOptions}
              value={selectedLocationValue}
              onChange={handleLocationChange}
              disabled={!canMultiLocation}
              primaryLocationName={primaryLocationName}
            />
          </Layout.Section>
        )}

        {analyticsQuery.isError && (
          <Layout.Section>
            <GatedQueryError
              title={t("在庫分析の取得に失敗しました")}
              onRetry={() => void analyticsQuery.refetch()}
            />
          </Layout.Section>
        )}

        {analyticsQuery.isLoading && (
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={8} />
            </Card>
          </Layout.Section>
        )}

        {showNotSynced && (
          <Layout.Section>
            <NotSyncedBanner onGoToDashboard={() => navigate("/dashboard")} />
          </Layout.Section>
        )}

        {!showNotSynced && analyticsQuery.data && (
          <>
            <Layout.Section>
              <ValuationSection valuation={analyticsQuery.data.valuation} />
            </Layout.Section>
            <Layout.Section>
              <AbcSection abc={analyticsQuery.data.abc_analysis} />
            </Layout.Section>
          </>
        )}

        {/* 未同期の空店では「滞留在庫はありません」も出さない＝データが無いだけで
            「無い」と断定したことになるため（NotSyncedBanner に畳む）。 */}
        {!showNotSynced && deadStockQuery.data && (
          <Layout.Section>
            <DeadStockSection report={deadStockQuery.data} />
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
